import React from 'react';

interface TaskBoardCardProps {
  requirement: any;
  onClick: () => void;
}

const STORY_TYPE_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  bug: {
    label: 'Bug',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 4.5v4M8 10.5v1" strokeWidth="1.5" stroke="currentColor" strokeLinecap="round" fill="none" />
      </svg>
    ),
  },
  task: {
    label: 'Task',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <rect x="2" y="2" width="12" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 8l2.5 2.5L11 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  story: {
    label: 'Story',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4 2h8a1 1 0 011 1v10.5a.5.5 0 01-.8.4L8 11l-4.2 2.9a.5.5 0 01-.8-.4V3a1 1 0 011-1z" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  spike: {
    label: 'Spike',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path d="M9 1.5L4 9h4l-1 5.5L12 7H8l1-5.5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  epic: {
    label: 'Epic',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path d="M9 1L4 8h4l-1 7L12 8H8l1-7z" fill="currentColor" opacity="0.9" />
      </svg>
    ),
  },
  'sub-task': {
    label: 'Sub-task',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-700/30',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <rect x="4" y="4" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6.5 8.5l1.5 1.5L10.5 7" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
};

const PRIORITY_BORDERS: Record<string, string> = {
  critical: 'border-l-red-500',
  high: 'border-l-orange-400',
  medium: 'border-l-blue-400',
  low: 'border-l-emerald-400',
};

const PRIORITY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-400',
  medium: 'bg-blue-400',
  low: 'bg-emerald-400',
};

export function TaskBoardCard({ requirement, onClick }: TaskBoardCardProps): React.ReactElement {
  const typeConfig = requirement.story_type ? STORY_TYPE_CONFIG[requirement.story_type] : null;
  const priorityBorder = PRIORITY_BORDERS[requirement.priority] || 'border-l-gray-300';
  const priorityDot = PRIORITY_DOT[requirement.priority] || 'bg-gray-300';
  const isOverdue = requirement.due_date && new Date(requirement.due_date) < new Date();

  // Time tracking progress
  const hasEstimate = requirement.original_estimate_hours > 0;
  const timeSpent = requirement.time_spent_hours || 0;
  const timeProgress = hasEstimate ? Math.min((timeSpent / requirement.original_estimate_hours) * 100, 100) : 0;
  const isOverTime = hasEstimate && timeSpent > requirement.original_estimate_hours;

  return (
    <div
      onClick={onClick}
      className={`${requirement.is_verified === false ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-white dark:bg-gray-800'} rounded-lg border ${requirement.is_verified === false ? 'border-yellow-300 dark:border-yellow-700' : 'border-gray-200 dark:border-gray-700'} border-l-[3px] ${priorityBorder} cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-150 group`}
    >
      {/* Parent story label (shown for subtasks) */}
      {requirement.parent_title && (
        <div className="px-3 pt-2 pb-0">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate block leading-tight">
            {requirement.parent_title}
          </span>
        </div>
      )}

      {/* Blocked badge */}
      {requirement.blocked_by_count > 0 && (
        <div className="px-3 pt-2 pb-0">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded"
            title={`Blocked by: ${requirement.blocked_by_codes}`}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Blocked
          </span>
        </div>
      )}

      {/* Header: story type + requirement code */}
      <div className={`px-3 ${requirement.parent_title ? 'pt-1' : requirement.blocked_by_count > 0 ? 'pt-1' : 'pt-2.5'} flex items-center gap-1.5`}>
        {typeConfig && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${typeConfig.color}`}>
            {typeConfig.icon}
            {typeConfig.label}
          </span>
        )}
        {typeConfig && requirement.requirement_code && (
          <span className="text-gray-300 dark:text-gray-600 text-[10px]">&middot;</span>
        )}
        {requirement.requirement_code && (
          <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate">
            {requirement.requirement_code}
          </span>
        )}
      </div>

      {/* Project name + Title */}
      <div className="px-3 pt-1 pb-1.5">
        {requirement.project_name && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500 block leading-tight mb-0.5">
            {requirement.project_name}
          </span>
        )}
        <h4 className="text-[13px] font-medium text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          {requirement.title}
        </h4>
      </div>

      {/* Labels */}
      {requirement.labels && (
        <div className="flex flex-wrap gap-1 px-3 pb-1.5">
          {requirement.labels.split(',').map((label: string, i: number) => (
            <span
              key={i}
              className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 rounded"
            >
              {label.trim()}
            </span>
          ))}
        </div>
      )}

      {/* Time tracking bar */}
      {hasEstimate && (
        <div className="px-3 pb-1">
          <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isOverTime
                  ? 'bg-red-400 dark:bg-red-500'
                  : timeProgress > 75
                  ? 'bg-amber-400 dark:bg-amber-500'
                  : 'bg-blue-400 dark:bg-blue-500'
              }`}
              style={{ width: `${timeProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer: avatar, priority, due date, points */}
      <div className="px-3 pb-2.5 pt-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {/* Assignee avatar */}
          {requirement.assigned_to_name ? (
            <span
              className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-gray-800"
              title={requirement.assigned_to_name}
            >
              {requirement.assigned_to_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
            </span>
          ) : (
            <span
              className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 text-[10px] flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-gray-800"
              title="Unassigned"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </span>
          )}

          {/* Priority dot */}
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${priorityDot}`} title={requirement.priority} />

          {/* Due date */}
          {requirement.due_date && (
            <span className={`text-[11px] truncate ${
              isOverdue
                ? 'bg-red-600 text-white font-semibold px-1.5 py-0.5 rounded-full'
                : 'text-gray-400 dark:text-gray-500'
            }`}>
              {new Date(requirement.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Time spent */}
          {timeSpent > 0 && (
            <span className={`text-[11px] tabular-nums ${isOverTime ? 'text-red-500 dark:text-red-400 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
              {timeSpent}h
            </span>
          )}
          {/* Story points */}
          {requirement.story_points != null && (
            <span className="text-[11px] font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 min-w-[22px] h-[22px] flex items-center justify-center rounded-full">
              {requirement.story_points}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskBoardCard;
